import { useEffect, useState } from "react";
import Services from "../services/services";
import { toast } from "react-toastify";
import { ArrowDownFromLine, ArrowUpToLine } from "lucide-react";
import { toastCustom, fetchUserData } from "../ultils";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

const isNull = (value) => value === null;
const SubscriptionCard = ({subData}) => {
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState(true);
    const [sub, setSub] = useState(subData);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    useEffect(() =>{
        setSub(subData);
    }, [subData]);

    const handleChangeSubName = async (sub) => {
        setLoading(true);
        try {
            const res = await Services.Subscription.updateName(sub.id, sub.name);
            const data = await res.json();
            if (res.ok && data.success) {
                toast.success("Subscription name updated successfully!");
            } else {
                toast.error(`code_${res.status}: ${data.error}`);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleChangeAutoRenew = async (id, is_renew_on_expired, is_renew_on_reached_limit) => {
        setLoading(true);
        try {
            const res = await Services.Subscription.changeAutoRenew(id, is_renew_on_expired, is_renew_on_reached_limit);
            const data = await res.json();
            if (res.ok && data.success) {
                toast.success("Changed! Please keep your balance enough for next renewal.");
                setSub((prev) => ({ ...data.data, current_price: prev.current_price }));
            } else {
                toast.error(`code(${res.status}): ${data.error ?? ""}`)
            }
        }
        finally {
            setLoading(false);
        }
    };

    const handleCheckRenew = async (requests_limit, concurrency, sub_id, is_freemium, service_callback) => {
        if (is_freemium === 1) {
            handleRenewSubscription(sub_id);
            return;
        }
        const res = await Services.Subscription.preview(service_callback, requests_limit, null, concurrency);
        const data = await res.json();
        if (res.ok && data.success) {
            toastCustom(
                (<>
                    Are you sure to spend ${data.data.price.toFixed(4)} to renew this plan?<br />
                    Limit: {requests_limit} requests<br />
                    Concurrency: {concurrency}<br />
                    Duration: 31 days<br />
                </>),
                [
                    {
                        label: "Confirm",
                        className: "bg-green-500 hover:bg-green-700 text-white",
                        onClick: () => handleRenewSubscription(sub_id)
                    },
                    {
                        label: "Cancel",
                        className: "bg-gray-500 hover:bg-gray-700 text-white",
                        onClick: () => toast.dismiss()
                    }
                ]
            );
        } else {
            toast.error(`code(${res.status}): ${data.error ?? ""}`)
        }
    };
    const handleRenewSubscription = async (sub_id) => {
        setLoading(true);
        try {
            const res = await Services.Subscription.renew(sub_id);
            const data = await res.json();
            if (res.ok && data.success) {
                toast.success("Subscription renewed successfully!");
                setSub((prev) => ({ ...data.data, current_price: prev.current_price }));
                fetchUserData(dispatch, navigate);
            } else {
                toast.error(`code(${res.status}): ${data.error ?? ""}`);
            }
        }
        finally {
            setLoading(false);
        }

    };
    let reqPercent = null;
    let reqText = null;
    if (!isNull(sub.requests_used) && !isNull(sub.requests_limit)) {
        reqPercent = (sub.requests_used / sub.requests_limit) * 100;
        if (sub.requests_limit >= 0)
            reqText = `Used ${reqPercent.toFixed(0)}% - Remains ${sub.requests_remain} calls`;
        else
            reqText = `Unlimited - Used ${sub.requests_used}`;
    }
    let daysPercent = null;
    let daysText = null;
    if (!isNull(sub.expired_at) && !isNull(sub.started_at)) {
        const now = new Date();
        const expired = new Date(sub.expired_at + "Z");
        const daysRemain = (expired - now) / (1000 * 60 * 60 * 24);
        const totalDays = 31;
        const daysUsed = totalDays - daysRemain;
        daysPercent = (daysUsed / totalDays) * 100;
        daysText = `Used ${daysPercent.toFixed(0)}% - Remains ${daysRemain.toFixed(2)} days ${sub.cycles_remain > 0 ? ` (+${sub.cycles_remain} cycle)` : ""}`;
    }
    return (
        <div className={`rounded-xl shadow-lg p-4 w-full max-w-lg mx-auto border border-gray-200 ${loading ? "opacity-50 cursor-not-allowed" : ""}`}>
            {/* Header */}
            <div className="flex flex-col gap-2">
                <div className="flex justify-between items-center hover:cursor-pointer hover:bg-gray-200 rounded-md p-2" onClick={() => setExpanded((e) => !e)}>
                    <span className="text-lg font-bold text-gray-700">#{sub.id} - {sub.service?.name || sub.service_callback}</span>
                    <span className="text-xs text-gray-400">{expanded ? <ArrowUpToLine /> : <ArrowDownFromLine />}</span>
                </div>
                <input className="border rounded px-2 py-1 w-full focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm font-semibold"
                    placeholder="Note..."
                    value={sub.name || ""}
                    onChange={(e) => setSub({ ...sub, name: e.target.value })}
                    onBlur={() => handleChangeSubName(sub)}
                />
            </div>
            {/* Summary */}
            <div className="mt-2 flex flex-col gap-3 text-sm">
                <div className="flex gap-2 text-sm">
                    <div className="flex gap-2 border p-1 rounded-lg w-full">
                        <span className="font-bold">Limit</span>
                        <span>{sub.requests_limit}</span>
                    </div>
                    <div className="flex gap-2 border p-1 rounded-lg w-full">
                        <span className="font-bold">Concurrency</span>
                        <span>{sub.concurrency}</span>
                    </div>
                </div>
                <div className="flex gap-2 text-sm">
                    <div className={`flex border-2 p-1 rounded-lg w-full items-center bg-${["blue-300 border-blue-500", "gray-300 border-gray-500"][sub.is_freemium]}`}>
                        <span className="w-full text-center">{sub.is_freemium ? "Freemium" : "Paid"}</span>
                    </div>
                    <div className={`flex border-2 p-1 rounded-lg w-full items-center bg-${["green-300 border-green-500", "yellow-500 border-yellow-700", "yellow-500 border-yellow-700"][sub.status]}`}>
                        <span className="w-full text-center">{["Active", "Expired", "Reached Limit"][sub.status]}</span>
                    </div>
                </div>
                <div className="flex flex-col gap-2 text-sm">
                    <div className="relative w-full h-6 bg-gray-200 border border-gray-300 rounded overflow-hidden">
                        <div
                            className="absolute top-0 left-0 h-full bg-green-300"
                            style={{
                                width: `${reqPercent}%`,
                                transition: "width 0.3s"
                            }}
                        ></div>
                        <div className="absolute inset-0 flex items-center justify-center text-black">
                            {reqText}
                        </div>
                    </div>
                    <div className="relative w-full h-6 bg-gray-200 border border-gray-300 rounded overflow-hidden">
                        <div
                            className="absolute top-0 left-0 h-full bg-blue-300"
                            style={{
                                width: `${daysPercent}%`,
                                transition: "width 0.3s"
                            }}
                        ></div>
                        <div className="absolute inset-0 flex items-center justify-center text-black">
                            {daysText}
                        </div>
                    </div>
                </div>
            </div>
            {/* Details */}
            {expanded && (
                <div className="mt-2 flex flex-col gap-3 text-sm">
                    <div className="flex flex-col items-center border p-1 rounded w-full gap-2">
                        <div className="flex gap-2">
                            <span className="font-bold">Started</span>
                            <span className="break-all">{new Date(sub.started_at).toLocaleString()}</span>
                        </div>
                        <div className="flex gap-2">
                            <span className="font-bold">Expired</span>
                            <span className="break-all">{new Date(sub.expired_at).toLocaleString()}</span>
                        </div>
                    </div>
                    <div className="flex flex-col justify-center text-center border border-gray-200 p-1 rounded-md">
                        <span className="text-center mb-1">
                            Auto-Renew Options
                        </span>
                        <div className="flex gap-1 text-sm">
                            <button
                                className={`font-semibold w-full h-full p-1 rounded border border-gray-400 ${sub.is_renew_on_expired ? "bg-green-500 text-white hover:bg-gray-700 hover:line-through no-underline" : "bg-gray-300 hover:bg-green-700 line-through hover:no-underline"}`}
                                onClick={() => handleChangeAutoRenew(sub.id, sub.is_renew_on_expired ? 0 : 1, sub.is_renew_on_reached_limit)}
                            >
                                on Expired
                            </button>
                            <button
                                className={`font-semibold w-full h-full p-1 rounded border border-gray-400 ${sub.is_renew_on_reached_limit ? "bg-green-500 text-white hover:bg-gray-700 hover:line-through no-underline" : "bg-gray-300 hover:bg-green-700 line-through hover:no-underline"}`}
                                onClick={() => handleChangeAutoRenew(sub.id, sub.is_renew_on_expired, sub.is_renew_on_reached_limit ? 0 : 1)}
                            >
                                on Reached Limit
                            </button>
                        </div>
                    </div>
                    <button className="w-full text-base font-semibold py-1 bg-blue-500 text-white rounded hover:bg-blue-700"
                        onClick={() => handleCheckRenew(sub.requests_limit, sub.concurrency, sub.id, sub.is_freemium, sub.service_callback)}>
                        Renew (${sub.current_price?.toFixed(2) ?? "-"})
                    </button>
                </div>
            )}
        </div>
    );
};

export default SubscriptionCard;